import type { ErrorEvent, EventHint } from '@sentry/react'

import { errorMessage, errorStatus } from '@/lib/errors'

const FINGERPRINT_NETWORK = 'np-network-error'
const FINGERPRINT_API = 'np-api-error'

/**
 * Native `fetch` network failures surface as a `TypeError` with one of these
 * browser-specific messages and carry NO HTTP response (so no `.status`). The
 * Sentry browser tracing integration can append the request target in
 * parentheses (`"Failed to fetch (api.example.com)"`), so matching accepts both
 * the native message and that enhanced form. They are transient client-side
 * conditions — offline, DNS, CORS, an aborted navigation — not application bugs.
 * We downgrade them to `warning` and collapse them into a single issue per
 * environment instead of Sentry's default of one bucket per target host + route.
 *
 * The `TypeError` guard is deliberate: an app-level `Error` such as
 * `"Failed to fetch image: 400"` is a real condition and must NOT be swept in
 * here, even though its message contains "Failed to fetch".
 */
const NETWORK_ERROR_MESSAGES = [
  'Failed to fetch', // Chrome / Edge
  'Load failed', // Safari
  'NetworkError when attempting to fetch resource.', // Firefox
  'The network connection was lost.', // iOS WebKit
  'The Internet connection appears to be offline.', // iOS WebKit
] as const

function isNetworkErrorMessage(message: string): boolean {
  return NETWORK_ERROR_MESSAGES.some(
    nativeMessage => message === nativeMessage || (message.startsWith(`${nativeMessage} (`) && message.endsWith(')')),
  )
}

function isNetworkError(error: unknown): boolean {
  return error instanceof TypeError && isNetworkErrorMessage(error.message)
}

/**
 * Collapse per-instance identifiers so one logical error maps to one Sentry
 * issue: `"Scenario with ID gj8251 not found"` and `"...with ID MdNxR not found"`
 * must share a fingerprint rather than fragmenting into one issue per id.
 *
 * Targets the shapes ids actually take in our backend messages, so tokens that
 * merely happen to contain a digit but carry meaning (`OAuth2`, `S3`, `v1`,
 * `2FA`, `"30 seconds"`) are left intact — collapsing those would merge
 * genuinely distinct errors. Order matters:
 *   1. UUIDs — their hyphens would otherwise split into several tokens.
 *   2. The token after a standalone `id`/`ID` — whatever its shape (hashid,
 *      numeric, uuid), which is how our "... with ID <x> ..." messages read.
 *   3. Long standalone numeric / hex runs that are ids on their own.
 */
export function normalizeErrorMessage(message: string): string {
  return message
    .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, '<id>')
    .replace(/\b(id)\s+\S+/gi, '$1 <id>')
    .replace(/\b\d{4,}\b/g, '<id>')
    .replace(/\b[0-9a-f]{8,}\b/gi, '<id>')
    .trim()
}

/**
 * Sentry `beforeSend` classifier — the single place that decides severity and
 * grouping for the errors our React Query caches and {@link logger} funnel into
 * Sentry.
 *
 * Why the fingerprint always carries `environment`: dev / staging / prod all
 * ship the SAME DSN, so they share one Sentry project, and `environment` is a
 * filter tag — NOT a grouping key. An explicit fingerprint overrides Sentry's
 * default grouping entirely, so without the environment component a dev
 * "Scenario not found" and a prod one would merge into a single issue. Including
 * it keeps each environment in its own bucket.
 *
 * Drops ONLY 401s — expected auth control-flow that fires on every logged-out
 * visitor's login/onboarding boot (`getMe`). Keeping them would flood Sentry and,
 * because the replay flush keys on event TYPE not level, upload a replay per
 * visit. Every other event is kept: benign client conditions are downgraded to
 * `warning` — other 4xx (expected user/flow errors) and network failures — while
 * 5xx and genuine JS exceptions stay at `error`. Genuine exceptions (no HTTP
 * status, not a network `TypeError`) keep Sentry's default stack-based grouping
 * and level.
 *
 * Returns `null` only for the dropped 401 case.
 */
export function classifyEvent(event: ErrorEvent, hint?: EventHint): ErrorEvent | null {
  const original = hint?.originalException
  const environment = event.environment ?? 'unknown'

  if (isNetworkError(original)) {
    event.level = 'warning'
    event.fingerprint = [FINGERPRINT_NETWORK, environment]
    return event
  }

  const status = errorStatus(original)
  if (typeof status === 'number') {
    // 401 is expected control-flow (every logged-out visitor's boot getMe on the
    // exempt auth/invite/terms paths). Drop it — not actionable, highest-volume.
    if (status === 401) {
      return null
    }
    event.level = status >= 500 ? 'error' : 'warning'
    event.fingerprint = [FINGERPRINT_API, environment, String(status), normalizeErrorMessage(errorMessage(original))]
    return event
  }

  return event
}
